import { BACKEND_URL } from "@/lib/api";
import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [role, setRole] = useState(null);
  const [err, setErr] = useState(null);

  const signInPath = role === "lender" ? "/lender/login" : role === "referrer" ? "/introducer/login" : "/login";

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw !== pw2) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/reset-password`, { token, password: pw });
      const who = r?.data?.role || null;
      setRole(who);
      setDone(true);
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
      const target = who === "lender" ? "/lender/login" : who === "referrer" ? "/introducer/login" : "/login";
      setTimeout(() => nav(target, { replace: true }), 2500);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Reset link is invalid or expired. Request a new one.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative bg-[#0F172A] text-white p-12 flex-col">
        <Link to="/" className="inline-flex items-center gap-2.5">
          <span className="inline-grid place-items-center h-8 w-8 rounded-md bg-white">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="#0F172A" strokeWidth="2">
              <path d="M4 7h7M4 12h12M4 17h7" strokeLinecap="round" /><circle cx="18" cy="7" r="2.2" fill="#0055FF" stroke="none" /><circle cx="18" cy="17" r="2.2" fill="#0055FF" stroke="none" />
            </svg>
          </span>
          <span className="font-semibold tracking-tight">Connexis<span className="text-[#0055FF]">.</span></span>
        </Link>
        <div className="mt-auto max-w-md">
          <p className="text-lg tracking-tight leading-snug">Choose something memorable — but not guessable.</p>
          <p className="text-sm text-slate-400 mt-3">8+ characters with a mix of letters, numbers and a symbol works well.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="reset-back-to-login">
            <ArrowLeft className="size-3.5" /> Back to sign in
          </Link>
          {!done ? (
            <>
              <div className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0055FF]">
                <ShieldCheck className="size-3.5" /> Account security
              </div>
              <h1 className="mt-3 text-3xl sm:text-4xl tracking-[-0.02em] font-semibold text-slate-900">Set a new password</h1>
              <p className="mt-2.5 text-sm text-slate-600">Almost there — pick a new password to finish resetting your Connexis account.</p>
              <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="reset-form">
                {err && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-800 flex items-start gap-2" data-testid="reset-error">
                    <AlertCircle className="size-4 mt-0.5 shrink-0" /><span>{err}</span>
                  </div>
                )}
                <div>
                  <Label htmlFor="pw">New password</Label>
                  <Input id="pw" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" data-testid="reset-pw" />
                </div>
                <div>
                  <Label htmlFor="pw2">Confirm new password</Label>
                  <Input id="pw2" type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw2" />
                </div>
                <Button disabled={busy} type="submit" className="w-full rounded-full h-11" data-testid="reset-submit">
                  {busy ? "Updating…" : <>Update password <ArrowRight className="size-4" /></>}
                </Button>
              </form>
            </>
          ) : (
            <div className="mt-12 text-center" data-testid="reset-done">
              <span className="inline-grid place-items-center size-14 rounded-full bg-emerald-50 text-emerald-600 mx-auto">
                <CheckCircle2 className="size-7" strokeWidth={1.6} />
              </span>
              <h1 className="mt-5 text-2xl sm:text-3xl tracking-[-0.02em] font-semibold text-slate-900">Password updated</h1>
              <p className="mt-2.5 text-sm text-slate-600">
                {role === "lender" ? "Taking you to your lender desk sign-in…"
                  : role === "referrer" ? "Taking you to your introducer portal sign-in…"
                  : "Redirecting you to sign in…"}
              </p>
              <Link to={signInPath} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0055FF] hover:underline" data-testid="reset-goto-signin">
                Sign in now <ArrowRight className="size-3.5" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
